/**
 * @fileoverview CommunicationSettings - Unified SMTP email configuration
 * Single SMTP configuration used for all system emails
 */
import React, { useState, useCallback } from 'react';
import {
    DmsTextField,
    DmsButton,
    useGet,
    intl,
    fetchData,
    HTTP_STATUS
} from 'dmsbasereact';
import { SettingsViewEdit } from '../../components/SettingsLayouts';
import { Grid, Typography, Box, Chip, Alert, CircularProgress } from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';

/**
 * SmtpViewContent - Display SMTP settings in read-only mode
 */
const SmtpViewContent = ({ data }) => {
    return (
        <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <EmailIcon color="primary" />
                <Typography variant="subtitle1" fontWeight="bold">
                    {intl.get('SmtpSettings.Title').d('Servidor de Email')}
                </Typography>
            </Box>
            <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="body2">
                    <strong>{intl.get('SmtpSettings.UsedFor').d('Utilizado para:')}</strong>{' '}
                    {intl.get('SmtpSettings.Description').d(
                        'Todos os emails do sistema: recuperação de senha, verificação de email, códigos MFA, aprovações de conta/instituição, alertas de documentos e notificações.'
                    )}
                </Typography>
            </Alert>
            {!data ? (
                <Alert severity="warning">
                    {intl.get('SmtpSettings.NotConfigured').d('O servidor SMTP ainda não foi configurado.')}
                </Alert>
            ) : (
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary">
                            {intl.get('Status').d('Status')}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Chip
                                label={data.server ? intl.get('Configured').d('Configured') : intl.get('NotConfigured').d('Not Configured')}
                                color={data.server ? 'success' : 'default'}
                                size="small"
                            />
                        </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary">
                            {intl.get('SmtpServer').d('Servidor SMTP')}
                        </Typography>
                        <Typography variant="body1">
                            {data.server || '-'}
                        </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary">
                            {intl.get('SmtpPort').d('Porta')}
                        </Typography>
                        <Typography variant="body1">
                            {data.port ?? '-'}
                        </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary">
                            {intl.get('SenderEmail').d('Email do Remetente')}
                        </Typography>
                        <Typography variant="body1">
                            {data.sender_email || '-'}
                        </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary">
                            {intl.get('LoginLink').d('Link de Login')}
                        </Typography>
                        <Typography variant="body1">
                            {data.login_link || '-'}
                        </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary">
                            {intl.get('LogoUrl').d('URL do Logo')}
                        </Typography>
                        <Typography variant="body1" sx={{ wordBreak: 'break-all' }}>
                            {data.logo_url || '-'}
                        </Typography>
                    </Grid>
                </Grid>
            )}
        </Box>
    );
};

/**
 * SmtpEditContent - Display SMTP settings as editable form fields
 */
const SmtpEditContent = ({ formData, onFieldChange, onTestConnection, testing, testResult, isConfigured }) => {
    return (
        <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <EmailIcon color="primary" />
                <Typography variant="subtitle1" fontWeight="bold">
                    {intl.get('SmtpSettings.Title').d('Servidor de Email')}
                </Typography>
            </Box>
            <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="body2">
                    <strong>{intl.get('SmtpSettings.UsedFor').d('Utilizado para:')}</strong>{' '}
                    {intl.get('SmtpSettings.Description').d(
                        'Todos os emails do sistema: recuperação de senha, verificação de email, códigos MFA, aprovações de conta/instituição, alertas de documentos e notificações.'
                    )}
                </Typography>
            </Alert>
            <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                    <DmsTextField
                        name="server"
                        label={intl.get('SmtpServer').d('Servidor SMTP')}
                        fullWidth
                        value={formData.server ?? ''}
                        onChange={(e) => onFieldChange('server', e.target.value)}
                        helperText={intl.get('SmtpServerHelp').d('Ex: smtp.office365.com')}
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <DmsTextField
                        name="port"
                        label={intl.get('SmtpPort').d('Porta')}
                        type="number"
                        fullWidth
                        value={formData.port ?? 587}
                        onChange={(e) => onFieldChange('port', e.target.value)}
                        helperText={intl.get('SmtpPortHelp').d('Geralmente 587 (TLS) ou 465 (SSL)')}
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <DmsTextField
                        name="sender_email"
                        label={intl.get('SenderEmail').d('Email do Remetente')}
                        fullWidth
                        value={formData.sender_email ?? ''}
                        onChange={(e) => onFieldChange('sender_email', e.target.value)}
                        helperText={intl.get('SenderEmailHelp').d('Endereço de origem dos emails')}
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <DmsTextField
                        name="sender_password"
                        label={intl.get('SmtpPassword').d('Senha')}
                        type="password"
                        fullWidth
                        value={formData.sender_password ?? ''}
                        onChange={(e) => onFieldChange('sender_password', e.target.value)}
                        helperText={intl.get('LeaveBlankToKeep').d('Leave blank to keep existing')}
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <DmsTextField
                        name="login_link"
                        label={intl.get('LoginLink').d('Link de Login')}
                        fullWidth
                        value={formData.login_link ?? ''}
                        onChange={(e) => onFieldChange('login_link', e.target.value)}
                        helperText={intl.get('LoginLinkHelp').d('URL para o botão de login nos emails')}
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <DmsTextField
                        name="logo_url"
                        label={intl.get('LogoUrl').d('URL do Logo')}
                        fullWidth
                        value={formData.logo_url ?? ''}
                        onChange={(e) => onFieldChange('logo_url', e.target.value)}
                        helperText={intl.get('LogoUrlHelp').d('URL da imagem do logo nos emails')}
                    />
                </Grid>
                <Grid item xs={12}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
                        <DmsButton
                            variant="secondary"
                            text={testing ? intl.get('Testing').d('Testing...') : intl.get('TestConnection').d('Test Connection')}
                            onClick={onTestConnection}
                            disabled={testing || !isConfigured}
                        />
                        {testing && <CircularProgress size={20} />}
                    </Box>
                    {testResult && (
                        <Alert severity={testResult.connection_ok ? 'success' : 'error'} sx={{ mt: 2 }}>
                            {testResult.message}
                        </Alert>
                    )}
                    {!isConfigured && (
                        <Alert severity="info" sx={{ mt: 2 }}>
                            {intl.get('SmtpNotConfigured').d('Salve as configurações primeiro para testar a conexão.')}
                        </Alert>
                    )}
                </Grid>
            </Grid>
        </Box>
    );
};

/**
 * CommunicationSettings - Unified SMTP configuration component
 */
export const CommunicationSettings = () => {
    const [testResult, setTestResult] = useState(null);
    const [testing, setTesting] = useState(false);
    const { data, isLoading, refetch } = useGet('/alert_smtp_settings', null, true);
    const settingsData = data?.data;

    const handleSave = async (payload) => {
        try {
            const response = await fetchData('/alert_smtp_settings', 'PUT', true, payload, false, true);
            if (response.status === HTTP_STATUS.OK_200 || response.status === HTTP_STATUS.CREATED_201) {
                setTestResult(null);
                return response.data.data;
            }
            return null;
        } catch (error) {
            return null;
        }
    };

    const handleTestConnection = useCallback(async () => {
        setTesting(true);
        setTestResult(null);
        try {
            const response = await fetchData('/alert_smtp_settings/test', 'POST', true);
            if (response.status === HTTP_STATUS.OK_200) {
                setTestResult(response.data?.data || response.data);
            } else {
                setTestResult({ connection_ok: false, message: response.data?.detail || intl.get("CommunicationSettings.TestFailed").d("Test failed") });
            }
        } catch (error) {
            setTestResult({ connection_ok: false, message: error.message });
        } finally {
            setTesting(false);
        }
    }, []);

    const isConfigured = Boolean(settingsData?.server);

    const EditContentWithTest = useCallback(({ formData, onFieldChange }) => (
        <SmtpEditContent
            formData={formData}
            onFieldChange={onFieldChange}
            onTestConnection={handleTestConnection}
            testing={testing}
            testResult={testResult}
            isConfigured={isConfigured}
        />
    ), [handleTestConnection, testing, testResult, isConfigured]);

    return (
        <SettingsViewEdit
            data={settingsData}
            loading={isLoading}
            title=""
            description=""
            viewContent={SmtpViewContent}
            editContent={EditContentWithTest}
            prepareFormData={(data) => ({
                server: data?.server ?? '',
                port: data?.port ?? 587,
                sender_email: data?.sender_email ?? '',
                sender_password: '',
                login_link: data?.login_link ?? '',
                logo_url: data?.logo_url ?? ''
            })}
            buildPayload={(formData, originalData) => {
                const payload = {
                    pk: originalData?.pk || 1,
                    server: formData.server,
                    port: parseInt(formData.port, 10),
                    sender_email: formData.sender_email,
                    login_link: formData.login_link,
                    logo_url: formData.logo_url
                };
                if (formData.sender_password && formData.sender_password.trim() !== '') {
                    payload.sender_password = formData.sender_password;
                }
                return payload;
            }}
            onSave={handleSave}
            onDataUpdated={refetch}
            confirmMessage={intl.get('ConfirmSaveSMTPMessage').d(
                'Tem certeza que deseja salvar as configurações de SMTP?'
            )}
        />
    );
};
